import React, { useState, useEffect, useRef } from 'react'
import { Users, CheckCircle, Clock, BarChart3, Loader2, Plus, Minus, Timer } from 'lucide-react'
import { apiClient } from '@/services/api'
import type { SelfPacedDashboardData, SelfPacedDashboardPlayer, SelfPacedDashboardActivity } from '@/types'

interface SelfPacedDashboardProps {
  lessonId: string
}

function formatTimeLimit(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

export function SelfPacedDashboard({ lessonId }: SelfPacedDashboardProps) {
  const [data, setData] = useState<SelfPacedDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const [extendingId, setExtendingId] = useState<string | null>(null)
  const [extendMinutes, setExtendMinutes] = useState(5)
  const [extendLoading, setExtendLoading] = useState(false)

  const fetchDashboard = async () => {
    try {
      const response = await apiClient.getSelfPacedDashboard(lessonId)
      if (response.data) {
        setData(response.data)
        setError(null)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
    pollRef.current = setInterval(fetchDashboard, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [lessonId])

  const handleExtendTime = async (activityId: string) => {
    setExtendLoading(true)
    try {
      await apiClient.extendActivityTime(lessonId, activityId, extendMinutes)
      setExtendingId(null)
      await fetchDashboard()
    } catch {
      // extension failed
    } finally {
      setExtendLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={fetchDashboard} className="text-blue-600 text-sm underline mt-2">
          Retry
        </button>
      </div>
    )
  }

  if (!data) return null

  const { players, activities, summary } = data

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-surface-deep rounded-lg border border-theme-border p-4">
          <div className="flex items-center gap-2 text-theme-muted text-sm mb-1">
            <Users className="w-4 h-4" />
            Total Students
          </div>
          <p className="text-2xl font-bold text-theme-text">{summary.total_students}</p>
        </div>
        <div className="bg-surface-deep rounded-lg border border-theme-border p-4">
          <div className="flex items-center gap-2 text-theme-muted text-sm mb-1">
            <BarChart3 className="w-4 h-4" />
            Avg Completion
          </div>
          <p className="text-2xl font-bold text-theme-text">{summary.average_completion}%</p>
        </div>
        <div className="bg-surface-deep rounded-lg border border-theme-border p-4">
          <div className="flex items-center gap-2 text-theme-muted text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Fully Completed
          </div>
          <p className="text-2xl font-bold text-green-600">{summary.fully_completed}</p>
        </div>
      </div>

      {/* Activity Completion Bars */}
      <div className="bg-surface-deep rounded-lg border border-theme-border p-4">
        <h3 className="text-sm font-semibold text-theme-text mb-3">Activity Completion</h3>
        <div className="space-y-3">
          {activities.map((activity: SelfPacedDashboardActivity) => (
            <div key={activity.id}>
              <div className="flex items-center gap-3">
                <span className="text-xs text-theme-muted w-6 text-right flex-shrink-0">{activity.sequence_number}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-theme-text truncate max-w-[200px]">{activity.title}</span>
                    <div className="flex items-center gap-2">
                      {activity.time_limit_seconds != null && (
                        <span className="text-theme-muted flex items-center gap-1">
                          <Timer className="w-3 h-3" />
                          {formatTimeLimit(activity.time_limit_seconds)}
                        </span>
                      )}
                      <span className="text-theme-muted">{activity.completed_count}/{activity.total_players}</span>
                    </div>
                  </div>
                  <div className="bg-surface-hover rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${activity.completion_percentage}%` }}
                    />
                  </div>
                </div>
                {/* Extend time toggle */}
                {activity.time_limit_seconds != null && (
                  <button
                    onClick={() => setExtendingId(extendingId === activity.id ? null : activity.id)}
                    className={`p-1 rounded text-xs transition-colors ${
                      extendingId === activity.id
                        ? 'text-blue-500 bg-blue-500/10'
                        : 'text-theme-muted hover:text-theme-text'
                    }`}
                    title="Extend time"
                  >
                    <Clock className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Extend time controls (inline) */}
              {extendingId === activity.id && (
                <div className="ml-9 mt-2 flex items-center gap-2">
                  <button
                    onClick={() => setExtendMinutes(m => Math.max(1, m - 1))}
                    className="w-6 h-6 flex items-center justify-center rounded border border-theme-border text-theme-muted hover:text-theme-text"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-xs font-medium text-theme-text w-12 text-center">{extendMinutes}m</span>
                  <button
                    onClick={() => setExtendMinutes(m => Math.min(60, m + 1))}
                    className="w-6 h-6 flex items-center justify-center rounded border border-theme-border text-theme-muted hover:text-theme-text"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleExtendTime(activity.id)}
                    disabled={extendLoading}
                    className="px-3 py-1 rounded text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                  >
                    {extendLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Extend'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Student Table */}
      <div className="bg-surface-deep rounded-lg border border-theme-border overflow-hidden">
        <div className="px-4 py-3 border-b border-theme-border bg-surface">
          <h3 className="text-sm font-semibold text-theme-text">Student Progress</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-theme-border bg-surface">
                <th className="text-left px-4 py-2 font-medium text-theme-text-muted">Student</th>
                <th className="text-left px-4 py-2 font-medium text-theme-text-muted">Current Activity</th>
                <th className="text-center px-4 py-2 font-medium text-theme-text-muted">Progress</th>
                <th className="text-center px-4 py-2 font-medium text-theme-text-muted">Score</th>
                <th className="text-center px-4 py-2 font-medium text-theme-text-muted">Status</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player: SelfPacedDashboardPlayer) => (
                <tr key={player.id} className="border-b border-theme-border hover:bg-surface">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${player.connection_status === 'connected' ? 'bg-green-400' : 'bg-theme-muted'}`} />
                      <span className="font-medium text-theme-text">{player.display_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-theme-text-muted">
                    {player.current_activity ? (
                      <span className="text-xs">{player.current_activity.title}</span>
                    ) : (
                      <span className="text-theme-text-muted text-xs">
                        {player.completion_percentage === 100 ? 'All done' : 'Not started'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 bg-surface-hover rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${player.completion_percentage === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${player.completion_percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-theme-muted w-8">{Math.round(player.completion_percentage)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center text-theme-text font-medium">{player.total_score}</td>
                  <td className="px-4 py-2 text-center">
                    {player.completion_percentage === 100 ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle className="w-3 h-3" /> Done
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-blue-600 text-xs font-medium">
                        <Clock className="w-3 h-3" /> In Progress
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {players.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-theme-muted">
                    No students have joined yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
